import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type Firestore,
  Timestamp,
} from "firebase/firestore";
import { normalizeText } from "@/lib/utils";
import {
  createGroupSchema,
  updateGroupSchema,
  type CreateGroupInput,
} from "@/lib/validation/schemas";
import type { Group, GroupMember } from "@/types/domain";

export interface GroupWithMembership {
  group: Group;
  membership: GroupMember;
}

export async function createGroup(
  db: Firestore,
  user: { uid: string; email: string; displayName?: string | null; photoURL?: string | null },
  values: CreateGroupInput,
) {
  const input = createGroupSchema.parse(values);
  const groupRef = doc(collection(db, "groups"));
  const memberRef = doc(groupRef, "members", user.uid);
  const activityRef = doc(collection(groupRef, "activities"));
  const optimisticNow = Timestamp.now();
  const group: Group = {
    id: groupRef.id,
    name: input.name,
    normalizedName: normalizeText(input.name),
    description: input.description ?? "",
    icon: input.icon,
    ownerId: user.uid,
    visibility: input.visibility,
    status: "active",
    createdAt: optimisticNow,
    updatedAt: optimisticNow,
    createdBy: user.uid,
    updatedBy: user.uid,
  };
  const membership: GroupMember = {
    id: user.uid,
    groupId: groupRef.id,
    userId: user.uid,
    email: user.email,
    displayName: user.displayName ?? user.email,
    photoURL: user.photoURL ?? undefined,
    role: "owner",
    status: "active",
    joinedAt: optimisticNow,
    updatedAt: optimisticNow,
  };

  const batch = writeBatch(db);
  const now = serverTimestamp();
  batch.set(groupRef, {
    ...group,
    createdAt: now,
    updatedAt: now,
  });
  batch.set(memberRef, {
    ...membership,
    photoURL: user.photoURL ?? null,
    joinedAt: now,
    updatedAt: now,
  });
  batch.set(activityRef, {
    id: activityRef.id,
    groupId: groupRef.id,
    actorId: user.uid,
    actorName: user.displayName ?? user.email,
    action: "group.created",
    entityType: "group",
    entityId: groupRef.id,
    entityName: input.name,
    createdAt: now,
  });
  await batch.commit();

  return { groupId: groupRef.id, group, membership };
}

export async function listUserGroups(db: Firestore, userId: string) {
  const [membershipResult, ownedResult] = await Promise.allSettled([
    getDocs(
      query(
        collectionGroup(db, "members"),
        where("userId", "==", userId),
        where("status", "==", "active"),
      ),
    ),
    getDocs(
      query(collection(db, "groups"), where("ownerId", "==", userId)),
    ),
  ]);

  if (membershipResult.status === "rejected" && ownedResult.status === "rejected") {
    throw membershipResult.reason;
  }

  const membershipGroups =
    membershipResult.status === "fulfilled"
      ? await Promise.all(
          membershipResult.value.docs.map(async (memberSnapshot) => {
            const membership = memberSnapshot.data() as GroupMember;
            const groupSnapshot = await getDoc(
              doc(db, "groups", membership.groupId),
            );
            if (!groupSnapshot.exists()) {
              return null;
            }
            return {
              group: groupSnapshot.data() as Group,
              membership,
            } satisfies GroupWithMembership;
          }),
        )
      : [];

  const ownedGroups =
    ownedResult.status === "fulfilled"
      ? await Promise.all(
          ownedResult.value.docs.map(async (groupSnapshot) => {
            const memberSnapshot = await getDoc(
              doc(db, "groups", groupSnapshot.id, "members", userId),
            );
            if (!memberSnapshot.exists()) {
              return null;
            }
            return {
              group: groupSnapshot.data() as Group,
              membership: memberSnapshot.data() as GroupMember,
            } satisfies GroupWithMembership;
          }),
        )
      : [];

  const byGroupId = new Map<string, GroupWithMembership>();
  for (const result of [...membershipGroups, ...ownedGroups]) {
    if (
      result?.group.status === "active" &&
      result.membership.status === "active"
    ) {
      byGroupId.set(result.group.id, result);
    }
  }

  return [...byGroupId.values()].sort((a, b) =>
    a.group.name.localeCompare(b.group.name),
  );
}

export async function getGroupWithMember(
  db: Firestore,
  groupId: string,
  userId: string,
) {
  const [groupSnapshot, memberSnapshot] = await Promise.all([
    getDoc(doc(db, "groups", groupId)),
    getDoc(doc(db, "groups", groupId, "members", userId)),
  ]);
  if (!groupSnapshot.exists() || !memberSnapshot.exists()) {
    return null;
  }
  return {
    group: groupSnapshot.data() as Group,
    membership: memberSnapshot.data() as GroupMember,
  } satisfies GroupWithMembership;
}

export async function updateGroup(
  db: Firestore,
  groupId: string,
  userId: string,
  values: { name: string; description?: string; icon: string },
) {
  const input = updateGroupSchema.parse(values);
  await updateDoc(doc(db, "groups", groupId), {
    ...input,
    description: input.description ?? "",
    normalizedName: normalizeText(input.name),
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}

export async function setGroupArchived(
  db: Firestore,
  groupId: string,
  userId: string,
  archived: boolean,
) {
  await updateDoc(doc(db, "groups", groupId), {
    status: archived ? "archived" : "active",
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}

export async function setGroupDeleted(
  db: Firestore,
  groupId: string,
  userId: string,
) {
  await updateDoc(doc(db, "groups", groupId), {
    status: "deleted",
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
}
